import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Store, BadgeCheck, ShoppingCart, CreditCard, Info, FileText, Tag } from "lucide-react";
import { toast } from "sonner";
import { MKT_GOLD, MKT_TINT } from "../../lib/marketplace";
import { apiPublicProducto, apiCheckout } from "../../lib/marketplaceApi";
import { CatalogCard, catLabel, precioTxt } from "./publicUi";
import { FavoriteButton } from "../../components/FavoriteButton";
import { SELLER_NOTICE } from "./mktui";
import Seo from "../../components/Seo";
import { humanizeAxiosError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

export default function ProductoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add, refresh } = useCart();
  const [data, setData] = useState(undefined);
  const [img, setImg] = useState(0);
  const [agregando, setAgregando] = useState(false);
  const [comprando, setComprando] = useState(false);

  useEffect(() => { apiPublicProducto(id).then(setData).catch(() => setData(null)); }, [id]);

  const agregarCarrito = async () => {
    if (!user || user === false) { toast.info("Inicia sesión para comprar."); navigate("/login"); return; }
    setAgregando(true);
    try {
      await add("producto", id);
      toast.success("Agregado al carrito.");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setAgregando(false); }
  };

  // "Comprar ahora": agrega el producto y va DIRECTO al pago (sin pasar por el carrito).
  const comprarAhora = async () => {
    if (!user || user === false) { toast.info("Inicia sesión para comprar."); navigate("/login"); return; }
    setComprando(true);
    try {
      await add("producto", id);
      const r = await apiCheckout(window.location.origin, true, "2026-07");
      if (r.url) { window.location.href = r.url; return; }
      if (r.gratis) {
        await refresh();
        toast.success("¡Listo! Tu recurso ya está en Mis Compras.");
        navigate(`/tienda/pago/exito?order_id=${r.order_id}`);
        return;
      }
      navigate("/tienda/carrito");
    } catch (e) { toast.error(humanizeAxiosError(e)); setComprando(false); }
  };

  if (data === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  if (!data) return <div className="min-h-screen flex flex-col items-center justify-center gap-3"><p className="text-slate-500">Contenido no disponible.</p><Link to="/tienda" className="font-bold" style={{ color: MKT_GOLD }}>Volver al Marketplace</Link></div>;

  const p = data.item;
  const galeria = [p.cover, ...(p.galeria || [])].filter(Boolean);
  const t = p.tienda || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="producto-detalle">
      <Seo title={p.titulo} description={p.descCorta || p.descCompleta} image={p.cover} type="product" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button onClick={() => navigate("/tienda")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ChevronLeft size={16} /> Volver al catálogo</button>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Galería */}
          <div>
            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white aspect-[4/3] flex items-center justify-center">
              {galeria[img] ? <img src={galeria[img]} alt={p.titulo} className="w-full h-full object-cover" /> : <FileText size={48} style={{ color: MKT_GOLD }} />}
            </div>
            {galeria.length > 1 && (
              <div className="mt-3 flex gap-2">
                {galeria.map((g, i) => (
                  <button key={i} onClick={() => setImg(i)} className="w-16 h-16 rounded-lg overflow-hidden border-2" style={{ borderColor: i === img ? MKT_GOLD : "#E2E8F0" }}>
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: MKT_GOLD }}>{catLabel(p.categoria)}</p>
            <h1 className="mt-1 text-2xl md:text-3xl font-extrabold text-slate-900 flex items-start justify-between gap-3">
              <span>{p.titulo}</span>
              <FavoriteButton item={{ tipo: "producto", ref_id: id, titulo: p.titulo, subtitulo: (p.tienda?.vendedor || p.tienda?.nombre || ""), enlace: `/tienda/producto/${id}`, imagen: p.cover || "" }} size={24} className="w-10 h-10 border border-slate-200 hover:bg-slate-50 flex-shrink-0" />
            </h1>
            <Link to={`/tienda/t/${t.id}`} className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
              <Store size={15} /> {t.vendedor || t.nombre} {t.verificado && <BadgeCheck size={14} className="text-emerald-600" />}
            </Link>
            {p.descCorta && <p className="mt-3 text-slate-600">{p.descCorta}</p>}

            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-3xl font-extrabold text-slate-900">{precioTxt(p)}</span>
              {p.precioPromo > 0 && p.precioPromo < p.precio && <span className="text-slate-400 line-through">S/ {Number(p.precio).toFixed(2)}</span>}
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button onClick={comprarAhora} disabled={comprando || agregando} data-testid="btn-comprar" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all disabled:opacity-60" style={{ backgroundColor: MKT_GOLD }}>{comprando ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} Comprar ahora</button>
              <button onClick={agregarCarrito} disabled={comprando || agregando} data-testid="btn-carrito" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-slate-300 text-slate-700 font-bold hover:bg-slate-100 transition-colors disabled:opacity-60">{agregando ? <Loader2 size={18} className="animate-spin" /> : <ShoppingCart size={18} />} Agregar al carrito</button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              {p.version && <Dato icon={Tag} label="Versión" value={p.version} />}
              {p.licencia && <Dato icon={FileText} label="Licencia" value={p.licencia} />}
            </div>

            <p className="mt-4 text-[11px] text-slate-400 flex items-start gap-1"><Info size={12} className="mt-0.5" /> {SELLER_NOTICE} La descarga estará disponible tras una compra válida.</p>
          </div>
        </div>

        {p.descCompleta && (
          <section className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-bold text-slate-900 mb-2">Descripción</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line">{p.descCompleta}</p>
          </section>
        )}
        {p.instrucciones && (
          <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-bold text-slate-900 mb-2">Instrucciones de uso</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line">{p.instrucciones}</p>
          </section>
        )}

        {data.relacionados?.length > 0 && (
          <section className="mt-8" data-testid="producto-relacionados">
            <h2 className="text-lg font-extrabold text-slate-900 mb-3">Productos relacionados</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.relacionados.map((i) => <CatalogCard key={i.id} item={i} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Dato({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-3">
      <p className="text-[11px] font-bold uppercase text-slate-400 inline-flex items-center gap-1"><Icon size={12} /> {label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}
